// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/ai-chat-protocol.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * The AYNI chatbot's wire contract, the same for every surface: the presenter
 * over IPC, the controller and the kiosk over the LAN WebSocket
 * (docs/AI_CHATBOT_PLAN.md). The conversation runs in the main process; a
 * surface sends a turn and receives its events. AYNI XR's website ('web')
 * runs the same engine in the page (plan §10).
 */
import { isAiChatLanguage, type AiChatLanguage } from './ai-languages';

export type AiChatSurface = 'presenter' | 'controller' | 'kiosk' | 'web';

/**
 * Who the chatbot is helping, for AYNI XR's Research and Education
 * environments (docs/AI_CHATBOT_RESEARCH_EDUCATION_CONTRACT.md §2). The host's
 * server resolves it; AYNI 3D and 2D pass none and keep today's behaviour.
 */
export type AiChatMode = 'general' | 'research' | 'teacher' | 'student';

export const AI_CHAT_MODES: ReadonlyArray<AiChatMode> = ['general', 'research', 'teacher', 'student'];

export interface AiChatModeOptions {
  mode: AiChatMode;
  /** Student mode only: guide with questions rather than answer (default true). */
  socratic?: boolean;
}

/** Longest message a visitor may send in one turn. */
export const MAX_AI_CHAT_TEXT_LENGTH = 1000;

export interface AiChatSendRequest {
  /** Chosen by the surface; one conversation per session. */
  sessionId: string;
  /** Chosen by the surface; every event of this turn carries it. */
  turnId: string;
  text: string;
  language: AiChatLanguage;
}

export type AiChatErrorCode =
  | 'AI_DISABLED'
  | 'AI_NOT_CONFIGURED'
  | 'AI_UNAVAILABLE'
  | 'AI_QUOTA'
  | 'AI_BUSY'
  /** This client asked more than its share in a short time (ai-chat-router.ts): wait a little. */
  | 'AI_RATE_LIMITED'
  | 'AI_INVALID_REQUEST'
  | 'AI_CANCELLED'
  | 'AI_TIMEOUT'
  | 'AI_FAILED';

/**
 * What a turn produces, in order: text as it streams, one event per action
 * the assistant takes, then exactly one `done` or `error`. A `done` from
 * 'safety' closes a turn the safety layer answered (a visitor in distress, a
 * declined question, a withheld reply; docs/AI_CHATBOT_SAFETY.md).
 */
export type AiChatEvent =
  | { type: 'delta'; sessionId: string; turnId: string; text: string }
  | { type: 'action'; sessionId: string; turnId: string; tool: string; ok: boolean; summary: string }
  | { type: 'done'; sessionId: string; turnId: string; source: 'model' | 'fast-path' | 'safety' }
  | { type: 'error'; sessionId: string; turnId: string; code: AiChatErrorCode; message: string };

/** Electron IPC channels between the presenter and main. */
export const AI_CHAT_IPC = {
  send: 'ai-chat-send',
  cancel: 'ai-chat-cancel',
  reset: 'ai-chat-reset',
  event: 'ai-chat-event',
  getConfig: 'ai-config-get',
  setConfig: 'ai-config-set'
} as const;

/**
 * WebSocket message types between the controller or kiosk and the server.
 * A turn is sent as ai_chat_send (data: AiChatSendRequest); its events come
 * back one message each as ai_chat_event (data: AiChatEvent). The server
 * sends ai_chat_ended (data: { reason: 'idle' }) when it ended the client's
 * conversations, e.g. a kiosk's visitor walked away: the screen starts over.
 */
export const AI_CHAT_WS = {
  send: 'ai_chat_send',
  cancel: 'ai_chat_cancel',
  reset: 'ai_chat_reset',
  event: 'ai_chat_event',
  ended: 'ai_chat_ended'
} as const;

/**
 * A window event (detail: AiChatConfigView) the presenter's settings card sends
 * after a save, so the AI tab can update its status line.
 */
export const AI_CONFIG_CHANGED_EVENT = 'ayni-ai-config-changed';

/** What the presenter's settings show; the key itself never leaves main. */
export interface AiChatConfigView {
  enabled: boolean;
  baseUrl: string;
  model: string;
  hasKey: boolean;
  /**
   * The licence brings the assistant: it goes to the AYNI gateway with the
   * licence's token, so the endpoint, model and key above are needed only for
   * another model server.
   */
  licensed: boolean;
  /** The speech endpoint (speech to text, text to speech); empty: text goes through the chat endpoint, and no voice. */
  speechBaseUrl: string;
}

export interface AiChatConfigPatch {
  enabled?: boolean;
  baseUrl?: string;
  model?: string;
  /** A new key; an empty string removes it. Write-only. */
  apiKey?: string;
  speechBaseUrl?: string;
}

function isId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,100}$/.test(value);
}

export function validateAiChatSendRequest(raw: unknown): { ok: true; request: AiChatSendRequest } | { ok: false; message: string } {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return { ok: false, message: 'Request must be an object.' };
  const { sessionId, turnId, text, language } = raw as Record<string, unknown>;
  if (!isId(sessionId)) return { ok: false, message: 'sessionId must be 1-100 letters, digits or _.:-' };
  if (!isId(turnId)) return { ok: false, message: 'turnId must be 1-100 letters, digits or _.:-' };
  if (typeof text !== 'string' || text.trim().length === 0) return { ok: false, message: 'text must not be empty.' };
  if (text.length > MAX_AI_CHAT_TEXT_LENGTH) return { ok: false, message: `text must be at most ${MAX_AI_CHAT_TEXT_LENGTH} characters.` };
  if (!isAiChatLanguage(language)) return { ok: false, message: 'language must be one of en, es, nl, ca.' };
  return { ok: true, request: { sessionId, turnId, text: text.trim(), language } };
}

// --- Speech (phase 5) -------------------------------------------------------

/**
 * Speech to text: the surface records the visitor's question (16 kHz mono WAV)
 * and main transcribes it through the assistant's endpoint, so the key stays in
 * main. The text then goes in as an ordinary turn.
 */
export const AI_SPEECH_IPC = { transcribe: 'ai-speech-transcribe', speak: 'ai-speech-speak' } as const;
export const AI_SPEECH_WS = {
  transcribe: 'ai_speech_transcribe',
  transcribed: 'ai_speech_transcribed',
  speak: 'ai_speech_speak',
  spoken: 'ai_speech_spoken'
} as const;

/** One piece of a reply at a time (the surface splits replies at sentences). */
export const MAX_AI_SPEAK_TEXT_LENGTH = 400;

export interface AiSpeechSpeakRequest {
  requestId: string;
  language: AiChatLanguage;
  text: string;
}

export type AiSpeechSpeakResult =
  | { requestId: string; ok: true; /** WAV: bytes over IPC, base64 over the WebSocket. */ audio: Uint8Array | string }
  | { requestId: string; ok: false; code: AiChatErrorCode; message: string };

export function validateAiSpeechSpeakRequest(raw: unknown): { ok: true; request: AiSpeechSpeakRequest } | { ok: false; message: string } {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return { ok: false, message: 'Request must be an object.' };
  const { requestId, language, text } = raw as Record<string, unknown>;
  if (!isId(requestId)) return { ok: false, message: 'requestId must be 1-100 letters, digits or _.:-' };
  if (!isAiChatLanguage(language)) return { ok: false, message: 'language must be one of en, es, nl, ca.' };
  if (typeof text !== 'string' || !text.trim()) return { ok: false, message: 'text must not be empty.' };
  if (text.length > MAX_AI_SPEAK_TEXT_LENGTH) return { ok: false, message: `text must be at most ${MAX_AI_SPEAK_TEXT_LENGTH} characters.` };
  return { ok: true, request: { requestId, language, text: text.trim() } };
}

/** About 60 s of 16 kHz mono 16-bit WAV. */
export const MAX_AI_SPEECH_AUDIO_BYTES = 2 * 1024 * 1024;

export interface AiSpeechTranscribeRequest {
  requestId: string;
  language: AiChatLanguage;
  /** The WAV file, base64 (WebSocket) or raw bytes (IPC). */
  audio: string | Uint8Array;
}

export type AiSpeechTranscribeResult =
  | { requestId: string; ok: true; text: string }
  | { requestId: string; ok: false; code: AiChatErrorCode; message: string };

function decodeBase64(text: string): Uint8Array | null {
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(text)) return null;
  try {
    const binary = atob(text);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
    return bytes;
  } catch {
    return null;
  }
}

export function validateAiSpeechTranscribeRequest(
  raw: unknown
): { ok: true; request: { requestId: string; language: AiChatLanguage; audio: Uint8Array } } | { ok: false; message: string } {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return { ok: false, message: 'Request must be an object.' };
  const { requestId, language, audio } = raw as Record<string, unknown>;
  if (!isId(requestId)) return { ok: false, message: 'requestId must be 1-100 letters, digits or _.:-' };
  if (!isAiChatLanguage(language)) return { ok: false, message: 'language must be one of en, es, nl, ca.' };
  const bytes = audio instanceof Uint8Array ? audio : typeof audio === 'string' && audio.length <= Math.ceil(MAX_AI_SPEECH_AUDIO_BYTES / 3) * 4 ? decodeBase64(audio) : null;
  if (!bytes || bytes.length === 0) return { ok: false, message: 'audio must be a WAV file (bytes, or base64 over the WebSocket).' };
  if (bytes.length > MAX_AI_SPEECH_AUDIO_BYTES) return { ok: false, message: `audio must be at most ${MAX_AI_SPEECH_AUDIO_BYTES} bytes.` };
  // "RIFF....WAVE": the one format every speech endpoint reads.
  const isWav = bytes.length > 12 && String.fromCharCode(...bytes.subarray(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.subarray(8, 12)) === 'WAVE';
  if (!isWav) return { ok: false, message: 'audio must be a WAV file.' };
  return { ok: true, request: { requestId, language, audio: bytes } };
}
